import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../core/services/auth.service';
import { firstValueFrom } from 'rxjs';

interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  is2faEnabled: boolean;
  twoFactorMethod: '2fa-sms' | '2fa-totp' | null;
}

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss'
})
export class SettingsComponent implements OnInit {
  profile: UserProfile | null = null;
  loading = true;
  actionLoading = false;
  errorMessage = '';
  successMessage = '';

  constructor(
    private router: Router,
    private http: HttpClient,
    private authService: AuthService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadProfile();
  }

  async loadProfile(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const response = await firstValueFrom(
        this.http.get<{ profile: UserProfile }>(`${environment.apiUrl}/auth/me`)
      );

      if (response?.profile) {
        this.profile = response.profile;
      }
    } catch (error: any) {
      console.error('Load profile error:', error);
      this.errorMessage = 'Failed to load profile settings';
    } finally {
      this.loading = false;
    }
  }

  navigateToSetup2FA(): void {
    this.router.navigate(['/setup-2fa']);
  }

  async disable2FA(): Promise<void> {
    if (!confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) {
      return;
    }

    // Prompt for verification code
    const code = prompt('Enter your current 2FA verification code to confirm:');
    if (!code) {
      return;
    }

    this.actionLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const response = await firstValueFrom(
        this.http.post<{ success: boolean; message: string }>(
          `${environment.apiUrl}/auth/2fa/disable`,
          { verificationCode: code }
        )
      );

      if (response.success) {
        this.successMessage = 'Two-factor authentication has been disabled';
        await this.loadProfile(); // Reload profile to update 2FA status
      }
    } catch (error: any) {
      console.error('Disable 2FA error:', error);
      this.errorMessage = error.error?.error || 'Failed to disable 2FA. Please check your verification code.';
    } finally {
      this.actionLoading = false;
    }
  }

  async regenerateBackupCodes(): Promise<void> {
    if (!confirm('Are you sure you want to regenerate backup codes? Your existing backup codes will no longer work.')) {
      return;
    }

    this.actionLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const response = await firstValueFrom(
        this.http.post<{ success: boolean; message: string; backupCodes: string[] }>(
          `${environment.apiUrl}/auth/2fa/backup-codes/regenerate`,
          {}
        )
      );

      if (response.success && response.backupCodes) {
        // Download backup codes automatically
        this.downloadBackupCodes(response.backupCodes);
        this.successMessage = 'New backup codes have been generated and downloaded';
      }
    } catch (error: any) {
      console.error('Regenerate backup codes error:', error);
      this.errorMessage = error.error?.error || 'Failed to regenerate backup codes';
    } finally {
      this.actionLoading = false;
    }
  }

  private downloadBackupCodes(codes: string[]): void {
    const codesText = codes.join('\n');
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'adn-backup-codes.txt';
    link.click();
    window.URL.revokeObjectURL(url);
  }

  get2FAMethodLabel(): string {
    if (!this.profile?.is2faEnabled) {
      return 'Not enabled';
    }

    switch (this.profile.twoFactorMethod) {
      case '2fa-totp':
        return 'Authenticator App';
      case '2fa-sms':
        return 'SMS Text Message';
      default:
        return 'Unknown';
    }
  }
}
